(() => {
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];

  $$('[data-vtx-dismiss]').forEach((button) => {
    button.addEventListener('click', () => button.closest('[data-vtx-notice]')?.remove());
  });

  const setupDisclosure = (button, fallbackSelector, focusSelector) => {
    const targetId = button.getAttribute('aria-controls');
    const target = (targetId && document.getElementById(targetId)) || $(fallbackSelector);
    if (!target) return;

    const toggle = (force) => {
      const willOpen = typeof force === 'boolean' ? force : target.classList.contains('hidden');
      target.classList.toggle('hidden', !willOpen);
      button.setAttribute('aria-expanded', String(willOpen));
      if (willOpen && focusSelector) $(focusSelector, target)?.focus();
    };

    button.addEventListener('click', () => toggle());
    target.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      toggle(false);
      button.focus();
    });
  };

  $$('[data-vtx-search-toggle]').forEach((button) => {
    setupDisclosure(button, '[data-vtx-search]', 'input[type="search"]');
  });

  $$('[data-vtx-menu-toggle]').forEach((button) => {
    setupDisclosure(button, '[data-vtx-mobile-menu]');
  });

  $$('[data-vtx-product]').forEach((root) => {
    let variants = [];
    try {
      variants = JSON.parse($('[data-vtx-variants-json]', root)?.textContent || '[]');
    } catch (error) {
      return;
    }
    if (!variants.length) return;

    const currency = root.dataset.currency || 'AUD';
    const cartAddUrl = root.dataset.cartAddUrl || '/cart/add.js';
    const cartUrl = root.dataset.cartUrl || '/cart';
    const quantityInput = $('[data-vtx-qty]', root);
    const productForm = $('.vtx-pdp-form', root);
    const initialVariantId = $('[data-vtx-variant-input]', root)?.value;
    let current = variants.find((variant) => String(variant.id) === initialVariantId) || variants[0];

    const formatMoney = (cents) => {
      try {
        return new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format((Number(cents) || 0) / 100);
      } catch (error) {
        return `${currency} ${((Number(cents) || 0) / 100).toFixed(2)}`;
      }
    };

    const setText = (selector, value) => {
      $$(selector, root).forEach((element) => {
        element.textContent = value;
      });
    };

    const getQuantity = () => Math.max(1, Number.parseInt(quantityInput?.value || '1', 10) || 1);

    const normalizeQuantity = () => {
      const quantity = getQuantity();
      if (quantityInput) quantityInput.value = String(quantity);
      return quantity;
    };

    const stockText = (variant) => {
      if (!variant.available) return 'Out of stock';
      if (variant.preorder) return 'Available to order · 2–3 week wait';
      if (variant.inventoryPolicy === 'continue' && variant.inventoryQuantity <= 0) return 'Available to order';
      return 'In stock';
    };

    const updateImage = (image, variant) => {
      if (!image || !variant.image) return;
      if (variant.imageSrcset) image.srcset = variant.imageSrcset;
      else image.removeAttribute('srcset');
      image.src = variant.image;
      if (variant.imageAlt) image.alt = variant.imageAlt;
    };

    const updateCoa = (variant) => {
      const hasCoa = Boolean(variant.coa);
      $$('[data-vtx-coa-link], [data-vtx-results-coa]', root).forEach((link) => {
        link.hidden = !hasCoa;
        if (hasCoa) link.href = variant.coa;
        else link.removeAttribute('href');
      });
      $$('[data-vtx-coa-pending], [data-vtx-results-coa-pending]', root).forEach((pending) => {
        pending.hidden = hasCoa;
      });
    };

    const updateFbtCount = () => {
      const selected = $$('[data-vtx-fbt]:checked:not(:disabled)', root).length;
      setText('[data-vtx-fbt-count]', `${selected} item${selected === 1 ? '' : 's'}`);
    };

    const updateTotals = () => {
      const quantity = getQuantity();
      setText('[data-vtx-total]', formatMoney(current.price * quantity));
      [
        ['two', root.dataset.d2],
        ['three', root.dataset.d3],
        ['four', root.dataset.d4],
      ].forEach(([key, discount]) => {
        const multiplier = 1 - (Number(discount) || 0) / 100;
        setText(`[data-vtx-bulk="${key}"]`, `${formatMoney(current.price * multiplier)} each`);
      });
    };

    const updateVariantUrl = (variant) => {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    };

    const updateVariant = (variant, updateUrl = true) => {
      current = variant;

      $$('[data-vtx-variant]', root).forEach((button) => {
        const isActive = String(button.dataset.vtxVariant) === String(variant.id);
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
      });

      const variantInput = $('[data-vtx-variant-input]', root);
      if (variantInput) variantInput.value = variant.id;

      setText('[data-vtx-price]', formatMoney(variant.price));
      setText('[data-vtx-mobile-price]', formatMoney(variant.price));
      setText('[data-vtx-size], [data-vtx-mobile-size], [data-vtx-placeholder-size], [data-vtx-panel-size]', variant.size);
      setText('[data-vtx-batch], [data-vtx-panel-batch], [data-vtx-results-batch]', variant.batch);
      setText('[data-vtx-hplc], [data-vtx-panel-hplc], [data-vtx-results-hplc]', variant.hplc);
      setText('[data-vtx-endotoxin], [data-vtx-results-endotoxin]', variant.endotoxin);
      setText('[data-vtx-sterility], [data-vtx-results-sterility]', variant.sterility);
      setText('[data-vtx-stock]', stockText(variant));
      const stock = $('[data-vtx-stock]', root);
      if (stock) stock.classList.toggle('is-preorder', Boolean(variant.preorder));
      setText('[data-vtx-analysis]', `◫ Analytical data: ${variant.hplc}`);

      const endotoxinBadge = $('[data-vtx-endotoxin-badge]', root);
      const sterilityBadge = $('[data-vtx-sterility-badge]', root);
      if (endotoxinBadge) endotoxinBadge.hidden = variant.endotoxin === 'Pending';
      if (sterilityBadge) sterilityBadge.hidden = variant.sterility === 'Pending';

      [$('[data-vtx-add]', root), $('[data-vtx-mobile-add]', root)].forEach((button) => {
        if (!button) return;
        button.disabled = !variant.available;
        button.textContent = variant.available ? 'Add to cart' : 'Out of stock';
      });

      updateImage($('.vtx-pdp-image', root), variant);
      updateCoa(variant);

      const currentFbt = $('[data-vtx-fbt-current]', root);
      if (currentFbt) {
        currentFbt.value = variant.id;
        currentFbt.disabled = !variant.available;
        if (!variant.available) currentFbt.checked = false;
        currentFbt.closest('.vtx-pdp-fbt-item')?.classList.toggle('is-selected', currentFbt.checked && !currentFbt.disabled);
      }
      setText('[data-vtx-fbt-current-price]', formatMoney(variant.price));
      setText('[data-vtx-fbt-current-size]', variant.size);

      const fbtMedia = $('[data-vtx-fbt-current-media]', root);
      if (fbtMedia && variant.image) {
        let fbtImage = $('img', fbtMedia);
        if (!fbtImage) {
          fbtImage = document.createElement('img');
          fbtImage.loading = 'lazy';
          fbtMedia.append(fbtImage);
        }
        updateImage(fbtImage, variant);
      }

      updateTotals();
      updateFbtCount();
      if (updateUrl) updateVariantUrl(variant);
    };

    $$('[data-vtx-variant]', root).forEach((button) => {
      button.addEventListener('click', () => {
        const variant = variants.find((item) => String(item.id) === button.dataset.vtxVariant);
        if (variant) updateVariant(variant);
      });
    });

    quantityInput?.addEventListener('input', updateTotals);
    quantityInput?.addEventListener('change', () => {
      normalizeQuantity();
      updateTotals();
    });
    productForm?.addEventListener('submit', normalizeQuantity);

    $('[data-vtx-minus]', root)?.addEventListener('click', () => {
      if (!quantityInput) return;
      quantityInput.value = String(Math.max(1, getQuantity() - 1));
      updateTotals();
    });

    $('[data-vtx-plus]', root)?.addEventListener('click', () => {
      if (!quantityInput) return;
      quantityInput.value = String(getQuantity() + 1);
      updateTotals();
    });

    const tabs = $$('[data-vtx-tab]', root);
    const activateTab = (tab, moveFocus = false) => {
      const panelId = tab.dataset.vtxTab;
      tabs.forEach((item) => {
        const isActive = item === tab;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-selected', String(isActive));
        item.tabIndex = isActive ? 0 : -1;
      });
      $$('[data-vtx-panel]', root).forEach((panel) => {
        const isActive = panel.dataset.vtxPanel === panelId;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });
      if (moveFocus) tab.focus();
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activateTab(tab));
      tab.addEventListener('keydown', (event) => {
        let nextIndex = null;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        if (nextIndex === null) return;
        event.preventDefault();
        activateTab(tabs[nextIndex], true);
      });
    });

    $$('[data-vtx-tab-jump]', root).forEach((button) => {
      button.addEventListener('click', () => {
        const tab = $(`[data-vtx-tab="${button.dataset.vtxTabJump}"]`, root);
        if (!tab) return;
        activateTab(tab);
        tab.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });

    $('[data-vtx-mobile-add]', root)?.addEventListener('click', () => productForm?.requestSubmit());

    $('[data-vtx-share]', root)?.addEventListener('click', async () => {
      try {
        if (navigator.share) await navigator.share({ title: document.title, url: window.location.href });
        else {
          await navigator.clipboard.writeText(window.location.href);
          window.alert('Product link copied');
        }
      } catch (error) {
        // A cancelled native share action requires no storefront error.
      }
    });

    $$('[data-vtx-fbt]', root).forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        checkbox.closest('.vtx-pdp-fbt-item')?.classList.toggle('is-selected', checkbox.checked && !checkbox.disabled);
        updateFbtCount();
      });
    });

    $('[data-vtx-add-all]', root)?.addEventListener('click', async (event) => {
      const button = event.currentTarget;
      const ids = $$('[data-vtx-fbt]:checked:not(:disabled)', root).map((checkbox) => Number(checkbox.value));
      if (!ids.length) return;

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: ids.map((id) => ({ id, quantity: 1 })) }),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.description || result.message || 'Unable to add the selected items.');
        window.location.assign(cartUrl);
      } catch (error) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        window.alert(error.message || 'Unable to add the selected items. Please try again.');
      }
    });

    updateVariant(current, false);
  });
})();
